import React, { FC, useState } from 'react'
import { Control, Controller } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import { Boxes } from 'transfix-ui/components/Boxes'
import { RadioOption } from 'transfix-ui/components/RadioOption/index'
import { Box, Typography } from 'transfix-ui/core'
import { IShipmentIssueFollower } from 'types/graphqlTypes'
import TagAutosuggest from 'apps/marketplace/components/TagAutosuggest'
import { useShipmentIssuesSuggestedOwnersQuery } from '../../ShipmentIssues/graphql/ShipmentIssuesSuggestedOwners'

export interface IOwner
  extends Pick<IShipmentIssueFollower, 'followerId' | 'followerType' | 'firstName' | 'lastName'> {}

interface IControlledIssueOwnerProps {
  control?: Control
  value?: IOwner | null
  placeholder?: string
  invalid?: Boolean
  validationMessage?: string
  parentShowOther?: boolean
  setParentShowOther?: (value: boolean) => void
  showSuggestedGroupOwners?: boolean
}

export const ControlledIssueOwner: FC<IControlledIssueOwnerProps> = ({
  control,
  invalid,
  placeholder,
  validationMessage,
  value: valueProp,
  parentShowOther = true,
  setParentShowOther = () => {},
  showSuggestedGroupOwners = true,
}) => {
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const [showOther, setShowOther] = useState<boolean>(false)

  let { data: { listOwners = [] } = {} } = useShipmentIssuesSuggestedOwnersQuery({
    variables: {
      shipmentId,
    },
  })

  if (!showSuggestedGroupOwners) {
    listOwners = listOwners.filter(({ owner }) => owner?.followerType !== 'Group')
  }

  const defaultValue = {
    followerId: valueProp?.followerId,
    followerType: valueProp?.followerType,
  }

  return (
    <Controller
      name='owner'
      control={control}
      defaultValue={defaultValue}
      render={({ onChange, value }) => {
        const selectOptionHandler = (newOwner: IOwner) => {
          setShowOther(false)
          setParentShowOther(false)
          onChange(newOwner)
        }

        const selectOtherUser = (user?: Partial<IAutocompleteOption>) => {
          if (user) {
            onChange({
              followerId: user.id,
              followerType: user.groupName,
              firstName: user.text,
            })
          }
        }

        const clickOtherOptionHandler = () => {
          setShowOther(true)
          setParentShowOther(true)
          onChange({})
        }

        const { followerId: valueFollowerId } = value || {}

        const isOwnerFromList = Boolean(
          listOwners.find(listOwner => listOwner?.owner?.followerId === valueFollowerId)
        )

        const isOtherSelected =
          (showOther && parentShowOther) || (valueFollowerId && !isOwnerFromList)

        return (
          <Box id='owner'>
            {invalid && (
              <Typography variant='caption' color='error'>
                {validationMessage}
              </Typography>
            )}
            {listOwners.map(({ owner: listOwner, role }) => {
              const selected = listOwner?.followerId === value?.followerId

              return (
                <RadioOption
                  key={listOwner?.followerId}
                  id={listOwner?.followerId || ''}
                  name={`${listOwner?.firstName} ${listOwner?.lastName}`}
                  value={listOwner?.followerId}
                  dataTest={`option-owner-${listOwner?.followerId}`}
                  selected={selected}
                  onChange={() => {
                    if (listOwner) {
                      selectOptionHandler(listOwner)
                    }
                  }}>
                  {`${listOwner?.firstName} ${listOwner?.lastName}`}
                  {role && <span className='text-secondary'> {role}</span>}
                </RadioOption>
              )
            })}
            <RadioOption
              id='other'
              name='other'
              value='0'
              dataTest='other'
              selected={isOtherSelected}
              onChange={clickOtherOptionHandler}>
              <Box>
                <Boxes.Mb1>Other</Boxes.Mb1>
                {isOtherSelected && (
                  <TagAutosuggest
                    value={{ id: value?.followerId }}
                    onChange={selectOtherUser}
                    placeholder={placeholder}
                  />
                )}
              </Box>
            </RadioOption>
          </Box>
        )
      }}
    />
  )
}

export default ControlledIssueOwner
